export const LISTING_IMAGE_QUALITY_STATUSES = new Set([
  "pending",
  "legacy_unreviewed",
  "unavailable",
  "pass",
  "warning",
  "fail",
  "manual_approved",
]);

export const LISTING_IMAGE_QUALITY_REASON_CODES = new Set([
  "clear_product",
  "image_quality",
  "watermark_or_text",
  "multiple_items_or_clutter",
  "restricted_content",
  "unsafe_or_inappropriate",
  "product_not_visible",
  "review_pending",
  "legacy_unreviewed",
  "review_unavailable",
  "manual_approved",
]);

const AFFIRMATIVE_ENV_VALUES = new Set(["true", "1", "yes", "on"]);
const PASS_MESSAGE = "The item is clear and ready for your listing.";
const WARNING_GENERIC_MESSAGE =
  "Photo may not be suitable. Make sure the item is clearly visible, the main focus, and shown in your own product photo.";
const WARNING_WEARABLE_PERSON_MESSAGE =
  "It’s okay to show clothing or accessories being worn, but make sure the item is clearly visible and this is your own product photo.";
const WARNING_STOCK_CATALOG_MESSAGE =
  "This looks like a catalog or stock-style image. Please use your own photo of the item when possible.";
const FAIL_UNCLEAR_ITEM_MESSAGE =
  "The item being sold is not clear enough. Please upload a photo where the product is the main focus.";
const FAIL_THIRD_PARTY_IMAGE_MESSAGE =
  "This appears to be a screenshot, marketplace image, or third-party promotional photo. Please upload your own product photo.";

export function isAffirmativeListingImageAiReviewValue(value) {
  return AFFIRMATIVE_ENV_VALUES.has(clean(value).toLowerCase());
}

export function isListingImageAiReviewEnabled(env = typeof process !== "undefined" ? process.env : {}) {
  const mode = clean(env?.LISTING_IMAGE_AI_REVIEW_MODE).toLowerCase();
  if (mode === "off") return false;
  return isAffirmativeListingImageAiReviewValue(env?.ENABLE_LISTING_IMAGE_AI_REVIEW);
}

// Required mode: a photo may only be published with a completed review of its exact bytes.
export function isListingImageAiReviewRequired(env = typeof process !== "undefined" ? process.env : {}) {
  return clean(env?.LISTING_IMAGE_AI_REVIEW_MODE).toLowerCase() === "required";
}

export function buildListingImageReviewUnavailableResult(options = {}) {
  const now = options.checkedAt || new Date().toISOString();
  return normalizeImageQualityResult({
    status: "unavailable",
    decision: "warning",
    reasonCode: "review_unavailable",
    score: 0,
    issues: [],
    suggestion: null,
    checkedAt: now,
    reviewedAt: null,
    provider: null,
    model: null,
    hash: options.hash || null,
    requestedAt: null,
    reviewState: "unavailable",
    sourceVariant: options.sourceVariant || "original",
    severity: "neutral",
    blocking: false,
    message: "Photo review is temporarily unavailable.",
    reason: options.reason || "provider_error",
    homepageEligible: false,
  });
}

export const LISTING_IMAGE_QUALITY_OUTCOMES = {
  clear_product: {
    status: "pass",
    badge: "Looks good",
    title: "Photo looks good",
    message: PASS_MESSAGE,
  },
  image_quality: {
    status: "warning",
    badge: "Review suggested",
    title: "Photo may need review",
    message: WARNING_GENERIC_MESSAGE,
  },
  watermark_or_text: {
    status: "warning",
    badge: "Review suggested",
    title: "Photo may not be suitable",
    message: WARNING_STOCK_CATALOG_MESSAGE,
  },
  multiple_items_or_clutter: {
    status: "warning",
    badge: "Review suggested",
    title: "Photo may need review",
    message: WARNING_GENERIC_MESSAGE,
  },
  restricted_content: {
    status: "fail",
    badge: "Replace photo",
    title: "Photo needs to be replaced",
    message: FAIL_UNCLEAR_ITEM_MESSAGE,
  },
  unsafe_or_inappropriate: {
    status: "fail",
    badge: "Replace photo",
    title: "Photo needs to be replaced",
    message: FAIL_UNCLEAR_ITEM_MESSAGE,
  },
  product_not_visible: {
    status: "fail",
    badge: "Replace photo",
    title: "Photo needs to be replaced",
    message: FAIL_UNCLEAR_ITEM_MESSAGE,
  },
  review_pending: {
    status: "pending",
    badge: "Checking photo…",
    title: "Checking photo…",
    message: "We’re reviewing this image before it can be published.",
  },
  legacy_unreviewed: {
    status: "legacy_unreviewed",
    badge: "Published photo",
    title: "Existing published photo",
    message: "This photo is already published. New or replaced photos will be reviewed before publishing.",
  },
  review_unavailable: {
    status: "unavailable",
    badge: "",
    title: "",
    message: "Photo review is temporarily unavailable. You can still publish this listing.",
  },
  manual_approved: {
    status: "manual_approved",
    badge: "Approved",
    title: "Photo looks good",
    message: PASS_MESSAGE,
  },
};

const WEARABLE_PERSON_PATTERN =
  /\b(apparel|clothing|shirt|dress|pants|jeans|jacket|coat|shoe|sneaker|boot|jewelry|jewellery|necklace|bracelet|ring|watch|accessor|hat|cap|bag|purse|wearable|worn|wearing|modeled|modelled|model|mannequin|held|holding)\b/;
const STOCK_CATALOG_PATTERN =
  /\b(stock|catalog|catalogue|brand|manufacturer|amazon|promotional|promo|ad|advertisement|marketplace|third-party|third party|screenshot|listing ui|watermark|logo|supplier)\b/;

function clean(value) {
  return String(value || "").trim();
}

const REASON_CODE_STATUS = Object.fromEntries(
  Object.entries(LISTING_IMAGE_QUALITY_OUTCOMES).map(([reasonCode, outcome]) => [
    reasonCode,
    outcome.status,
  ])
);

export function normalizeImageQualityStatus(value, fallback = "pending") {
  const status = clean(value).toLowerCase();
  return LISTING_IMAGE_QUALITY_STATUSES.has(status) ? status : fallback;
}

function inferReasonCodeFromIssues(status, issues) {
  const issueText = (issues || []).join(" ").toLowerCase();
  if (status === "pass") return "clear_product";
  if (status === "manual_approved") return "manual_approved";
  if (status === "pending") return "review_pending";
  if (status === "legacy_unreviewed") return "legacy_unreviewed";
  if (status === "unavailable") return "review_unavailable";
  if (status === "fail") {
    if (/restricted|cannabis|tobacco|alcohol|weapon|drug|regulated|counterfeit/.test(issueText)) {
      return "restricted_content";
    }
    if (/unsafe|explicit|nudity|sexual|violence|graphic|hate|inappropriate/.test(issueText)) {
      return "unsafe_or_inappropriate";
    }
    return "product_not_visible";
  }
  if (/watermark|logo|text|screenshot|supplier|marketplace|graphic/.test(issueText)) {
    return "watermark_or_text";
  }
  if (/multiple|clutter|accessor|packaging|background|main item|focus/.test(issueText)) {
    return "multiple_items_or_clutter";
  }
  return "image_quality";
}

function issueTextFromQuality(quality) {
  return (quality?.issues || []).join(" ").toLowerCase();
}

function buildOutcome(status, badge, title, message) {
  return { status, badge, title, message };
}

export function normalizeImageQualityReasonCode(value, status = "pending", issues = []) {
  const reasonCode = clean(
    value?.reasonCode ||
      value?.reason_code ||
      value?.image_quality_reason_code ||
      value?.reason ||
      value
  ).toLowerCase();
  if (LISTING_IMAGE_QUALITY_REASON_CODES.has(reasonCode)) return reasonCode;
  return inferReasonCodeFromIssues(status, issues);
}

export function normalizeImageQualityResult(value) {
  if (!value || typeof value !== "object") {
    return {
      status: "pending",
      reasonCode: "review_pending",
      score: null,
      issues: [],
      suggestion: null,
      checkedAt: null,
      reviewedAt: null,
      provider: null,
      model: null,
      hash: null,
      requestedAt: null,
      reviewState: null,
      sourceVariant: "original",
      homepageEligible: false,
    };
  }

  const rawStatus = value.status || value.decision || value.image_quality_status;
  const rawReasonCode = clean(
    value.reasonCode ||
      value.reason_code ||
      value.image_quality_reason_code ||
      value.reason
  ).toLowerCase();
  const inferredStatus = LISTING_IMAGE_QUALITY_REASON_CODES.has(rawReasonCode)
    ? REASON_CODE_STATUS[rawReasonCode]
    : null;
  const status = normalizeImageQualityStatus(rawStatus, inferredStatus || "pending");
  const score = Number(value.score ?? value.image_quality_score);
  const issues = Array.isArray(value.issues || value.image_quality_issues)
    ? (value.issues || value.image_quality_issues)
        .map((issue) => clean(issue))
        .filter(Boolean)
        .slice(0, 8)
    : [];
  const checkedAt = clean(value.checkedAt || value.image_quality_checked_at) || null;
  const reviewedAt = clean(value.reviewedAt || value.reviewed_at || checkedAt) || null;
  const reasonCode = normalizeImageQualityReasonCode(value, status, issues);
  const sourceVariant = clean(value.sourceVariant || value.source_variant) || "original";

  return {
    status,
    reasonCode,
    score: Number.isFinite(score) ? Math.max(0, Math.min(100, Math.round(score))) : null,
    issues,
    suggestion: clean(value.suggestion || value.image_quality_suggestion) || null,
    checkedAt,
    reviewedAt,
    provider: clean(value.provider || value.image_quality_provider) || null,
    model: clean(value.model || value.image_quality_model) || null,
    hash: clean(value.hash || value.image_quality_hash) || null,
    requestedAt: clean(value.requestedAt || value.image_quality_requested_at) || null,
    reviewState: clean(value.reviewState || value.image_quality_review_state) || null,
    sourceVariant,
    severity: clean(value.severity) || (status === "unavailable" ? "neutral" : null),
    blocking:
      value.blocking === true
        ? true
        : value.blocking === false
          ? false
          : status === "fail",
    message:
      clean(value.message) ||
      (status === "unavailable"
        ? "Photo review is temporarily unavailable. You can still publish this listing."
        : null),
    reason: clean(value.reason) || null,
    homepageEligible:
      value.homepageEligible === true ||
      value.homepage_eligible === true ||
      value.homepageEligible === "true",
  };
}

export function canUseImageQualityAsHomepageCover(quality) {
  const status = normalizeImageQualityStatus(quality?.status || quality?.image_quality_status);
  return status === "pass" || status === "manual_approved";
}

export function canUseImageQualityAsListingCover(quality) {
  const status = normalizeImageQualityStatus(quality?.status || quality?.image_quality_status);
  return status !== "fail";
}

export function isFinalImageQualityStatus(status) {
  return ["pass", "warning", "fail", "unavailable", "legacy_unreviewed", "manual_approved"].includes(
    normalizeImageQualityStatus(status)
  );
}

export function mergeImageQualityResult(current, incoming) {
  const normalizedCurrent = current ? normalizeImageQualityResult(current) : null;
  const normalizedIncoming = incoming ? normalizeImageQualityResult(incoming) : null;
  if (!normalizedIncoming) return normalizedCurrent;
  if (!normalizedCurrent) return normalizedIncoming;

  const currentIsFinal = isFinalImageQualityStatus(normalizedCurrent.status);
  const incomingIsFinal = isFinalImageQualityStatus(normalizedIncoming.status);
  if (incomingIsFinal && !currentIsFinal) return normalizedIncoming;
  if (!incomingIsFinal && currentIsFinal) return normalizedCurrent;
  if (incomingIsFinal && currentIsFinal) {
    const currentTime = Date.parse(normalizedCurrent.reviewedAt || normalizedCurrent.checkedAt || "");
    const incomingTime = Date.parse(normalizedIncoming.reviewedAt || normalizedIncoming.checkedAt || "");
    if (Number.isFinite(currentTime) && Number.isFinite(incomingTime) && incomingTime < currentTime) {
      return normalizedCurrent;
    }
    return normalizedIncoming;
  }

  const currentRequestedTime = Date.parse(normalizedCurrent.requestedAt || normalizedCurrent.checkedAt || "");
  const incomingRequestedTime = Date.parse(normalizedIncoming.requestedAt || normalizedIncoming.checkedAt || "");
  if (
    Number.isFinite(currentRequestedTime) &&
    Number.isFinite(incomingRequestedTime) &&
    incomingRequestedTime < currentRequestedTime
  ) {
    return normalizedCurrent;
  }
  return normalizedIncoming;
}

export function getListingImageQualityOutcome(quality) {
  const normalized = normalizeImageQualityResult(quality);
  const issueText = issueTextFromQuality(normalized);
  if (normalized.status === "warning" && STOCK_CATALOG_PATTERN.test(issueText)) {
    return buildOutcome(
      "warning",
      "Review suggested",
      "Photo may not be suitable",
      WARNING_STOCK_CATALOG_MESSAGE
    );
  }
  if (normalized.status === "warning" && WEARABLE_PERSON_PATTERN.test(issueText)) {
    return buildOutcome(
      "warning",
      "Review suggested",
      "Photo may need review",
      WARNING_WEARABLE_PERSON_MESSAGE
    );
  }
  if (normalized.status === "fail" && STOCK_CATALOG_PATTERN.test(issueText)) {
    return buildOutcome(
      "fail",
      "Replace photo",
      "Photo needs to be replaced",
      FAIL_THIRD_PARTY_IMAGE_MESSAGE
    );
  }
  const outcome = LISTING_IMAGE_QUALITY_OUTCOMES[normalized.reasonCode];
  if (outcome && outcome.status === normalized.status) return outcome;
  return LISTING_IMAGE_QUALITY_OUTCOMES[inferReasonCodeFromIssues(normalized.status, normalized.issues)];
}

export function getListingImageQualityLabel(quality) {
  return getListingImageQualityOutcome(quality).badge;
}

export function getListingImageQualityTitle(quality) {
  return getListingImageQualityOutcome(quality).title;
}

export function getListingImageQualitySuggestion(quality) {
  return getListingImageQualityOutcome(quality).message;
}

export function getListingImageQualityEligibilityMessage(quality) {
  return getListingImageQualityTitle(quality);
}
